// Refunding session credits when a class is canceled.
//
// Sessions are deducted at check-in, not at reservation, so anyone who checked
// into a class that later gets canceled has paid for a session that never
// happened. Every path that sets status='canceled' must call this, or the
// charge silently sticks.
//
// refundedAt on the attendance record makes it idempotent: a
// cancel -> reactivate -> cancel cycle credits once, not twice.
import { db } from '../firebase';
import {
  collection, getDocs, getDoc, doc, query, where, updateDoc, writeBatch, increment} from 'firebase/firestore';
import { logEvent } from './logEvent';

export async function refundCheckInsForClasses(classIds, { onAthleteBalance } = {}) {
  const ids = (classIds || []).filter(Boolean);
  if (!ids.length) return { refunded: 0, athletes: 0, athleteIds: [] };

  const recs = [];
  for (let i = 0; i < ids.length; i += 30) {
    const snap = await getDocs(query(
      collection(db, 'attendance'), where('classId', 'in', ids.slice(i, i + 30))
    ));
    snap.docs.forEach(d => {
      const a = d.data();
      if (a.type === 'coach' || !a.checkedInAt || a.refundedAt) return;
      recs.push({ id: d.id, ...a });
    });
  }
  if (!recs.length) return { refunded: 0, athletes: 0, athleteIds: [] };

  // Group by athlete: canceling a series can hit one athlete several times, and
  // writing each record's balanceBefore independently would let the last write
  // win — crediting one session instead of all of them.
  const byAthlete = {};
  for (const r of recs) {
    if (!r.athleteId) continue;
    (byAthlete[r.athleteId] = byAthlete[r.athleteId] || []).push(r);
  }

  let refunded = 0;
  for (const [athleteId, list] of Object.entries(byAthlete)) {
    try {
      const athDoc = await getDoc(doc(db, 'athletes', athleteId));
      if (!athDoc.exists()) continue;
      // Give back exactly what this class took, rather than computing a total
      // from a read that a concurrent check-in may already have invalidated.
      await updateDoc(doc(db, 'athletes', athleteId), { sessionBalance: increment(list.length) });
      // For the caller's on-screen number only. The write above is authoritative
      // and its result is not readable without another fetch; this is the best
      // estimate from what we last saw, and a refresh corrects it.
      const restored = (athDoc.data().sessionBalance || 0) + list.length;
      const batch = writeBatch(db);
      list.forEach(r => batch.update(doc(db, 'attendance', r.id), {
        refundedAt: new Date(), refundReason: 'class canceled',
      }));
      await batch.commit();
      refunded += list.length;
      if (onAthleteBalance) onAthleteBalance(athleteId, restored);
    } catch (e) {
      console.error('Session refund failed for athlete', athleteId, e);
    }
  }

  const athleteIds = Object.keys(byAthlete);
  if (refunded) {
    logEvent('class.refunded', {
      description: `Refunded ${refunded} session${refunded === 1 ? '' : 's'} to `
        + `${athleteIds.length} athlete${athleteIds.length === 1 ? '' : 's'} after cancellation`,
      metadata: { classIds: ids, refunded, athleteIds },
    });
  }
  return { refunded, athletes: athleteIds.length, athleteIds };
}
